import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { SceneConfig } from '../types/sceneConfig';
import RealEstateViewer from '../components/RealEstateViewer';
import { I18nProvider } from '../i18n';
import { S3_BUCKET_NAME, S3_REGION } from '../config/env';

const SINGLE_SCENE_CAMERA: Record<string, {
  pos?: [number, number, number];
  lookAt?: [number, number, number];
}> = {
  'medical_lake': {
    pos: [-2255.77, 765.00, 3771.53],
    lookAt: [-2283.86, 736.95, 3777.16],
  },
  'medical_lake_neighbourhood': {
    pos: [-2311.245, 780.077, 3777.299],
    lookAt: [-2287.213, 743.008, 3773.227],
  },
  'medical_lake_house': {
    pos: [-2285, 900, 3777],
    lookAt: [-2285, 736, 3777],
  },
};

type RawScene = Partial<SceneConfig> & { _id?: string; category?: string };

function validCameraTuple(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 3 || !value.every(Number.isFinite)) return undefined;
  return value as [number, number, number];
}

function toSceneConfig(apiScene: RawScene): SceneConfig {
  const resolvedSceneId = apiScene.sceneId ?? apiScene.sceneName ?? apiScene._id;
  if (!resolvedSceneId) throw new Error('Scene is missing a sceneId or sceneName');

  const sceneKey = resolvedSceneId.toLowerCase();
  const cameraDefaults = Object.entries(SINGLE_SCENE_CAMERA).find(([k]) => k === sceneKey)?.[1] ?? {};

  return {
    sceneId:   resolvedSceneId,
    sceneName: apiScene.sceneName ?? resolvedSceneId,
    gaus:      apiScene.gaus ?? undefined,
    manifest:  apiScene.manifest ?? undefined,
    audio:     apiScene.audio ?? undefined,
    envMap:    apiScene.envMap ?? undefined,
    imagePoses: apiScene.imagePoses ?? undefined,
    thumbnail: apiScene.thumbnail ?? undefined,
    defaultCameraPos:  sceneKey === 'medical_lake_neighbourhood'
      ? cameraDefaults.pos
      : validCameraTuple(apiScene.defaultCameraPos) ?? cameraDefaults.pos,
    defaultLookAt:     sceneKey === 'medical_lake_neighbourhood'
      ? cameraDefaults.lookAt
      : validCameraTuple(apiScene.defaultLookAt) ?? cameraDefaults.lookAt,
    sceneType:      apiScene.sceneType ?? undefined,
    coordinateFrame: apiScene.coordinateFrame ?? undefined,
    hasGeoAlignment: apiScene.hasGeoAlignment ?? false,
    supportsRecording: apiScene.supportsRecording ?? false,
  };
}

const DEFAULT_SCENE = 'medical_lake';

const RealEstateViewerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sceneId = searchParams.get('scene') ?? DEFAULT_SCENE;
  const gausOverride = searchParams.get('gausOverride');

  const [sceneConfig, setSceneConfig] = useState<SceneConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchScene = async () => {
      try {
        setError(null);
        setSceneConfig(null);

        const res = await customerApi.searchCollectiv3Scenes(sceneId);
        const sceneKey = sceneId.toLowerCase();
        const match = res.success
          ? (res.data as Collectiv3Scene[] | undefined)?.find(
              (s) => s.sceneName.toLowerCase() === sceneKey || s.sceneId?.toLowerCase() === sceneKey
            )
          : undefined;

        if (!match) throw new Error(`Scene "${sceneId}" not found`);
        if (cancelled) return;

        const resolved = toSceneConfig(match as RawScene);

        if (gausOverride) {
          const overrideUrl = gausOverride.startsWith('http')
            ? gausOverride
            : `https://${S3_BUCKET_NAME}.s3.${S3_REGION}.amazonaws.com/${gausOverride}`;
          resolved.gaus = overrideUrl;
        }

        setSceneConfig(resolved);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load scene');
      }
    };

    fetchScene();
    return () => { cancelled = true; };
  }, [sceneId, gausOverride]);

  const handleBack = () => navigate(-1);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!sceneConfig) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950">
        <Loader2 className="w-8 h-8 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <I18nProvider>
      <div style={{ width: '100vw', height: '100vh' }}>
        <RealEstateViewer scene={sceneConfig} onBack={handleBack} isPopupMode={!!gausOverride} />
      </div>
    </I18nProvider>
  );
};

export default RealEstateViewerPage;
